import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";

import {
  AppConfiguration,
  ConfigurationApiResponseFlat,
  formatAppName,
  generateDescription,
  ProcessedService,
} from "../types";

const API_BASE_URL = getAppPlatformUrl("app-configurations");
const REDACTED_FIELDS_KEY = "_redacted";
const SALEOR_AUTH_PLUGIN_ID_KEY = "_saleorAuthPluginId";
const SALEOR_REFRESH_TOKEN_KEY = "_saleorRefreshToken";

export const REDACTED_SECRET_PLACEHOLDER = "•••• (set)";

// Static app definitions that should always be available
const STATIC_SERVICES: ProcessedService[] = [
  // Internal apps
  {
    app_name: "dealer_locator",
    display_name: formatAppName("dealer_locator"),
    configurations: {},
    is_active: false,
    type: "internal",
    description: generateDescription("dealer_locator"),
  },
  // External apps (Google services)
  {
    app_name: "google_analytics",
    display_name: formatAppName("google_analytics"),
    configurations: {},
    is_active: false,
    type: "external",
    description: generateDescription("google_analytics"),
  },
  {
    app_name: "google_tag_manager",
    display_name: formatAppName("google_tag_manager"),
    configurations: {},
    is_active: false,
    type: "external",
    description: generateDescription("google_tag_manager"),
  },
  {
    app_name: "google_adsense",
    display_name: formatAppName("google_adsense"),
    configurations: {},
    is_active: false,
    type: "external",
    description: generateDescription("google_adsense"),
  },
  {
    app_name: "google_maps",
    display_name: formatAppName("google_maps"),
    configurations: {},
    is_active: false,
    type: "external",
    description: generateDescription("google_maps"),
  },
  {
    app_name: "google_search_console",
    display_name: formatAppName("google_search_console"),
    configurations: {},
    is_active: false,
    type: "external",
    description: generateDescription("google_search_console"),
  },
  {
    app_name: "google_merchant",
    display_name: formatAppName("google_merchant"),
    configurations: {},
    is_active: false,
    type: "external",
    description: generateDescription("google_merchant"),
  },
];

interface ConfigurationUpdateRequest {
  tenant: string;
  app_name: string;
  configurations: Record<string, any>;
  is_active: boolean;
}

const getTenantUrl = () => getApiUrl();

const getStoredSaleorRefreshToken = () =>
  typeof window === "undefined" ? null : window.localStorage.getItem(SALEOR_REFRESH_TOKEN_KEY);

const getStoredSaleorAuthPluginId = () =>
  typeof window === "undefined" ? null : window.localStorage.getItem(SALEOR_AUTH_PLUGIN_ID_KEY);

const parseSaleorGraphqlResponse = async <T>(response: Response): Promise<T> => {
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(`Saleor token refresh failed with ${response.status}`);
  }

  return body as T;
};

const saleorGraphqlRequest = async <T>(body: Record<string, unknown>): Promise<T> => {
  const response = await fetch(getTenantUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  return parseSaleorGraphqlResponse<T>(response);
};

const refreshSaleorAuthToken = async () => {
  const refreshToken = getStoredSaleorRefreshToken();

  if (!refreshToken) {
    throw new Error("Missing Saleor session. Sign in again before saving app configuration.");
  }

  const authPluginId = getStoredSaleorAuthPluginId();

  if (authPluginId) {
    const result = await saleorGraphqlRequest<{
      data?: { externalRefresh?: { token?: string | null; refreshToken?: string | null } | null };
    }>({
      query: `
        mutation ExternalRefresh($pluginId: String!, $input: JSONString!) {
          externalRefresh(pluginId: $pluginId, input: $input) {
            token
            refreshToken
          }
        }
      `,
      variables: {
        pluginId: authPluginId,
        input: JSON.stringify({ refreshToken }),
      },
    });
    const token = result.data?.externalRefresh?.token;

    if (!token) {
      throw new Error("Unable to refresh Saleor auth token.");
    }

    const nextRefreshToken = result.data?.externalRefresh?.refreshToken;

    if (nextRefreshToken) {
      window.localStorage.setItem(SALEOR_REFRESH_TOKEN_KEY, nextRefreshToken);
    }

    return token;
  }

  const result = await saleorGraphqlRequest<{
    data?: { tokenRefresh?: { token?: string | null } | null };
  }>({
    query: `
      mutation RefreshToken($refreshToken: String!) {
        tokenRefresh(refreshToken: $refreshToken) {
          token
        }
      }
    `,
    variables: { refreshToken },
  });
  const token = result.data?.tokenRefresh?.token;

  if (!token) {
    throw new Error("Unable to refresh Saleor auth token.");
  }

  return token;
};

export const getRedactedFields = (config?: AppConfiguration | null): string[] => {
  const topLevelRedacted = Array.isArray(config?._redacted) ? config?._redacted : [];
  const configRedacted = Array.isArray(config?.configurations?.[REDACTED_FIELDS_KEY])
    ? config?.configurations?.[REDACTED_FIELDS_KEY]
    : [];

  return Array.from(new Set([...(topLevelRedacted ?? []), ...(configRedacted ?? [])]));
};

export const withRedactedPlaceholders = (
  configurations: Record<string, any> = {},
  redactedFields: string[] = [],
) => {
  const normalizedConfigurations = { ...configurations };

  delete normalizedConfigurations[REDACTED_FIELDS_KEY];

  redactedFields.forEach(field => {
    if (normalizedConfigurations[field] == null) {
      normalizedConfigurations[field] = "";
    }
  });

  if (redactedFields.length > 0) {
    normalizedConfigurations[REDACTED_FIELDS_KEY] = redactedFields;
  }

  return normalizedConfigurations;
};

export const sanitizeConfigurationsForSave = (configurations: Record<string, any> = {}) => {
  const redactedFields = Array.isArray(configurations[REDACTED_FIELDS_KEY])
    ? configurations[REDACTED_FIELDS_KEY]
    : [];

  return Object.entries(configurations).reduce<Record<string, any>>((result, [key, value]) => {
    if (key === REDACTED_FIELDS_KEY) {
      return result;
    }

    if (redactedFields.includes(key) && (value === "" || value == null)) {
      return result;
    }

    result[key] = value;

    return result;
  }, {});
};

const hasRedactedConfigField = (configurations: Record<string, any> = {}, field: string) =>
  Array.isArray(configurations[REDACTED_FIELDS_KEY]) &&
  configurations[REDACTED_FIELDS_KEY].includes(field);

export const postConfigurationUpdate = async (
  requestData: ConfigurationUpdateRequest,
): Promise<Response> => {
  const tenant = requestData.tenant || getTenantUrl();
  const saleorAuthToken = await refreshSaleorAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${saleorAuthToken}`,
    "saleor-api-url": tenant,
  };

  return fetch(`${API_BASE_URL}/app/update-configuration`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      ...requestData,
      tenant,
      configurations: sanitizeConfigurationsForSave(requestData.configurations),
    }),
  });
};

export const updateServiceConfiguration = async (service: ProcessedService): Promise<void> => {
  const tenant = getTenantUrl();

  if (!tenant) {
    throw new Error("API_URL environment variable is not configured");
  }

  const requestData: ConfigurationUpdateRequest = {
    tenant,
    app_name: service.app_name,
    configurations: service.configurations || {},
    is_active: service.is_active,
  };

  try {
    const response = await postConfigurationUpdate(requestData);

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        `API request failed: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }
  } catch (error) {
    console.error(`Failed to update configuration for ${service.app_name}:`, error);
    throw error;
  }
};

// Smart update function that handles dealer_locator creation when needed
export const updateServiceConfigurationSmart = async (
  service: ProcessedService,
  createDealerLocatorCallback?: () => Promise<boolean>,
): Promise<void> => {
  // If this is dealer_locator being enabled, check if it exists first
  if (service.app_name === "dealer_locator" && service.is_active && createDealerLocatorCallback) {
    try {
      // Check if dealer_locator exists in the current API response
      const currentServices = await getServiceConfigurations();
      const dealerLocatorExists = currentServices.some(
        s =>
          s.app_name === "dealer_locator" &&
          (s.configurations?.token || hasRedactedConfigField(s.configurations, "token")),
      );

      if (!dealerLocatorExists) {
        console.warn("Dealer locator being enabled but doesn't exist, creating app first...");

        const created = await createDealerLocatorCallback();

        if (!created) {
          throw new Error("Failed to create dealer locator app");
        }

        console.warn("Dealer locator app created successfully");

        // The creation callback should handle the POST request, so we're done
        return;
      }
    } catch (error) {
      console.error("Error checking/creating dealer_locator:", error);
      throw error;
    }
  }

  // For all other cases (Google apps, or dealer_locator that already exists), just update normally
  await updateServiceConfiguration(service);
};

export const updateAllServiceConfigurations = async (
  services: ProcessedService[],
): Promise<void> => {
  const updatePromises = services.map(service => updateServiceConfiguration(service));

  try {
    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Failed to update some service configurations:", error);
    throw error;
  }
};

// Helper function to compare if two services are different
const hasServiceChanged = (original: ProcessedService, updated: ProcessedService): boolean => {
  // Compare is_active status
  if (original.is_active !== updated.is_active) {
    return true;
  }

  // Compare configurations (deep comparison)
  const originalConfigs = JSON.stringify(original.configurations || {});
  const updatedConfigs = JSON.stringify(updated.configurations || {});

  return originalConfigs !== updatedConfigs;
};

// More efficient function that only updates changed services
export const updateChangedServiceConfigurations = async (
  originalServices: ProcessedService[],
  updatedServices: ProcessedService[],
  createDealerLocatorCallback?: () => Promise<boolean>,
): Promise<void> => {
  // Find services that have actually changed
  const changedServices = updatedServices.filter(updatedService => {
    const originalService = originalServices.find(
      orig => orig.app_name === updatedService.app_name,
    );

    if (!originalService) {
      // Service is new (shouldn't happen in our case, but handle it)
      return true;
    }

    return hasServiceChanged(originalService, updatedService);
  });

  if (changedServices.length === 0) {
    console.warn("No services have changed, skipping API calls");

    return;
  }

  console.warn(
    `Updating ${changedServices.length} changed service(s):`,
    changedServices.map(s => s.app_name),
  );

  // Update services one by one to handle dealer_locator creation properly
  for (const service of changedServices) {
    try {
      await updateServiceConfigurationSmart(service, createDealerLocatorCallback);
    } catch (error) {
      console.error(`Failed to update ${service.app_name}:`, error);
      throw error;
    }
  }
};

export const getServiceConfigurations = async (): Promise<ProcessedService[]> => {
  const tenant = getTenantUrl();

  if (!tenant) {
    throw new Error("API_URL environment variable is not configured");
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/app/get-configuration?tenant=${encodeURIComponent(tenant)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        `API request failed: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const apiResponse: ConfigurationApiResponseFlat = await response.json();

    // Ensure safe handling of API response - now it's a flat array
    const safeApiResponse = Array.isArray(apiResponse) ? apiResponse : [];

    // Always merge API state with static services - static services will always show even if API returns empty arrays
    const mergedServices: ProcessedService[] = STATIC_SERVICES.map(staticService => {
      // Find matching service from API response
      const apiService = safeApiResponse.find(config => config.app_name === staticService.app_name);

      return {
        ...staticService,
        is_active: apiService?.is_active ?? false,
        configurations: withRedactedPlaceholders(
          {
            ...staticService.configurations,
            ...apiService?.configurations,
          },
          getRedactedFields(apiService),
        ),
      };
    });

    return mergedServices;
  } catch (error) {
    console.error("Failed to load service configurations:", error);

    // Return static services even on error - apps should always be available for configuration
    return STATIC_SERVICES;
  }
};
